/**
 * Queue and event analytics processing functions
 */
import { formatStatusDisplay, determineEventStatus, getDisplayValue } from './formatters';
import { getEmptyEventStats, getEmptyQueueAnalytics } from './emptyStates';

/**
 * Process queue data for analytics
 * @param {Array} queueData - Array of queue entries with embedded event data
 * @returns {Object} - Processed analytics data
 */
export const processQueueDataForAnalytics = (queueData) => {
  if (!Array.isArray(queueData)) return getEmptyQueueAnalytics();

  const analytics = {
    totalEntries: queueData.length,
    statusBreakdown: {},
    eventBreakdown: {},
    patientFlow: {
      completed: 0,
      archived: 0,
      pending: 0,
      inProgress: 0
    },
    eventDetails: {},
    timeDistribution: {}
  };

  // Process each queue entry
  queueData.forEach(entry => {
    const { status, eventId } = entry;

    // Count status breakdown
    analytics.statusBreakdown[status] = (analytics.statusBreakdown[status] || 0) + 1;

    // Update patient flow
    switch (status?.toLowerCase()) {
      case 'completed':
        analytics.patientFlow.completed++;
        break;
      case 'archived':
        analytics.patientFlow.archived++;
        break;
      case 'pending':
      case 'pending_review':
        analytics.patientFlow.pending++;
        break;
      case 'in_progress':
        analytics.patientFlow.inProgress++;
        break;
      default:
        break;
    }

    // Process event data
    if (eventId) {
      const eventKey = eventId.id || eventId._id?.toString();
      
      if (eventKey) {
        if (!analytics.eventBreakdown[eventKey]) {
          analytics.eventBreakdown[eventKey] = 0;
        }
        analytics.eventBreakdown[eventKey]++;

        if (!analytics.eventDetails[eventKey]) {
          analytics.eventDetails[eventKey] = {
            id: eventKey,
            title: eventId.title || 'Unknown Event',
            startDate: eventId.startDate,
            endDate: eventId.endDate,
            duration: eventId.duration,
            registrationStatus: eventId.registrationStatus,
            patientCount: 0,
            statusCounts: {}
          };
        }

        analytics.eventDetails[eventKey].patientCount++;
        const eventDetail = analytics.eventDetails[eventKey];
        eventDetail.statusCounts[status] = (eventDetail.statusCounts[status] || 0) + 1;

        if (eventId.startDate) {
          const month = new Date(eventId.startDate).toLocaleString('default', { month: 'short' });
          analytics.timeDistribution[month] = (analytics.timeDistribution[month] || 0) + 1;
        }
      }
    }
  });

  return analytics;
};

/**
 * Format queue analytics for dashboard display
 * @param {Object} queueAnalytics - Raw queue analytics
 * @returns {Object} - Formatted analytics for display
 */
export const formatQueueAnalyticsForDisplay = (queueAnalytics) => {
  const { eventDetails, statusBreakdown, patientFlow, timeDistribution } = queueAnalytics;

  // Convert event details to array for easier display
  const eventsArray = Object.values(eventDetails).map(event => ({
    ...event,
    startDateFormatted: event.startDate ? new Date(event.startDate).toLocaleDateString() : 'No date',
    endDateFormatted: event.endDate ? new Date(event.endDate).toLocaleDateString() : 'No end date',
    durationDays: event.duration ? Math.ceil(event.duration / (1000 * 60 * 60 * 24)) : 0,
    completionRate: event.statusCounts.completed ? 
      ((event.statusCounts.completed / event.patientCount) * 100).toFixed(1) : '0'
  }));

  // Format time distribution for charts
  const timeChartData = Object.entries(timeDistribution).map(([month, count]) => ({
    month,
    count,
    label: month
  }));

  // Calculate efficiency metrics
  const totalProcessed = patientFlow.completed + patientFlow.archived;
  const efficiencyRate = queueAnalytics.totalEntries > 0 ? 
    ((totalProcessed / queueAnalytics.totalEntries) * 100).toFixed(1) : '0';

  return {
    summary: {
      totalEntries: queueAnalytics.totalEntries,
      eventsCount: Object.keys(eventDetails).length,
      completedCount: patientFlow.completed,
      archivedCount: patientFlow.archived,
      pendingCount: patientFlow.pending,
      efficiencyRate: parseFloat(efficiencyRate)
    },
    events: eventsArray,
    statusDistribution: Object.entries(statusBreakdown).map(([status, count]) => ({
      status: formatStatusDisplay(status),
      count,
      percentage: ((count / queueAnalytics.totalEntries) * 100).toFixed(1)
    })),
    timeDistribution: timeChartData,
    patientFlow
  };
};

/**
 * Extract event analytics from queue data for the event statistics endpoint
 * @param {Array} queueData - Queue data array
 * @returns {Object} - Event statistics in expected format
 */
export const extractEventAnalyticsFromQueue = (queueData) => {
  if (!Array.isArray(queueData)) return getEmptyEventStats();

  const eventMap = new Map();
  const statusCounts = {
    total: queueData.length,
    active: 0,
    completed: 0,
    archived: 0
  };

  // Process queue data
  queueData.forEach(entry => {
    const { status, eventId } = entry;

    switch (status?.toLowerCase()) {
      case 'completed':
        statusCounts.completed++;
        break;
      case 'archived':
        statusCounts.archived++;
        break;
      case 'in_progress':
      case 'pending':
        statusCounts.active++;
        break;
      default:
        break;
    }

    if (eventId) {
      const eventKey = eventId.id || eventId._id?.toString();
      
      if (eventKey && !eventMap.has(eventKey)) {
        eventMap.set(eventKey, {
          id: eventKey,
          title: eventId.title || 'Unknown Event',
          startDate: eventId.startDate,
          endDate: eventId.endDate,
          attendees: 0,
          status: determineEventStatus(eventId),
          location: getDisplayValue(eventId.location, 'location')
        });
      }

      if (eventKey) {
        eventMap.get(eventKey).attendees++;
      }
    }
  });

  const events = Array.from(eventMap.values());
  const activeEvents = events.filter(event => event.status === 'active').length;
  const totalAttendees = events.reduce((sum, event) => sum + event.attendees, 0);
  
  const avgAttendance = events.length > 0 ? 
    (totalAttendees / events.length) : 0;

  return {
    total: events.length,
    active: activeEvents,
    completed: events.filter(event => event.status === 'completed').length,
    avgAttendance: parseFloat(avgAttendance.toFixed(1)),
    totalAttendees,
    byType: {
      health_screening: events.length,
      blood_drive: 0,
      educational_workshop: 0
    },
    upcomingEvents: events
      .filter(event => new Date(event.startDate) > new Date())
      .slice(0, 5)
      .map(event => ({
        id: event.id,
        title: event.title,
        date: event.startDate,
        location: event.location,
        expectedAttendees: event.attendees
      })),
    allEvents: events
  };
};
